import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Loader } from '@alfalab/core-components-loader';
import cn from 'classnames';
import { pluralize } from '@alfalab/utils';

import { GameProps, RunnerSettings } from '../common/types';
import { useWindowResize } from '../../core/hooks/useWindowResize';

import { drawGround, drawBackground, drawImage } from '../common/utils/drawers';
import {
  loadTexture,
  loadTextures,
  getRandomInteger,
  intersects,
  Rect,
  formatScore,
} from '../common/utils/helpers';

import { getEnvSize, CHARACTER_SIZE, CLOUD_SIZE } from './helpers';

import styles from './runner.module.css';

type RunnerGameProps = GameProps & {
  settings: RunnerSettings;
};

type CharacterState = 'IDLE' | 'RUN' | 'JUMP';

type EnvInfo = Array<{
  position: [number, number];
  size: [number, number];
  env: HTMLImageElement;
}>;

const MAX_ATTEMPTS_DEFAULT = 2;

export const Runner: FC<RunnerGameProps> = ({
  settings: { backgroundImgSrc, groundImgSrc, character, attempts, mainGameSettings },
  started,
  startGame,
  endGame,
}) => {
  const [attempt, setAttempt] = useState(0);
  const characterState = useRef<CharacterState>('IDLE');
  const level = useRef(1);
  const [score, setScore] = useState(0);
  const lastGoingEnvIndex = useRef(-1);
  const [crashScreenVisible, setCrashScreenVisible] = useState(false);
  const [getPrizeInProgress, setGetPrizeInProgress] = useState(false);

  // time
  const startJump = useRef(0);

  const [canvasSize, setCanvasSize] = useState<[number, number]>([0, 0]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const context = useRef<CanvasRenderingContext2D | null>(null);

  const [groundImage, setGroundImage] = useState<HTMLImageElement | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [characterIdleImages, setCharacterIdleImages] = useState<HTMLImageElement[]>([]);
  const [characterRunImages, setCharacterRunImages] = useState<HTMLImageElement[]>([]);
  const [characterJumpImages, setCharacterJumpImages] = useState<HTMLImageElement[]>([]);
  const [cloudImage, setCloudImage] = useState<HTMLImageElement | null>(null);
  const [envImage, setEnvImage] = useState<HTMLImageElement | null>(null);
  const crash = useRef(false);
  const maxScore = useRef(0);

  const envInfo = useRef<EnvInfo>([]);

  const mainRequestId = useRef(0);
  const characterRequestId = useRef(0);

  const groundY = canvasSize[1] - 125;

  const characterPosition = useRef<[number, number]>([0, 0]);
  const cloudPosition = useRef<[number, number]>([-200, -20]);

  const currentCharacterImage = useRef<HTMLImageElement | null>(null);

  const MAX_ATTEMPTS = attempts ?? MAX_ATTEMPTS_DEFAULT;

  const characterSrcSet: Map<CharacterState, HTMLImageElement[]> = new Map([
    ['IDLE', characterIdleImages],
    ['RUN', characterRunImages],
    ['JUMP', characterJumpImages],
  ]);

  useWindowResize(() => {
    if (wrapperRef.current) {
      setCanvasSize([wrapperRef.current.clientWidth, wrapperRef.current.clientHeight]);
    }
  });

  useLayoutEffect(() => {
    if (wrapperRef.current) {
      setCanvasSize([wrapperRef.current.clientWidth, wrapperRef.current.clientHeight]);
    }

    if (canvasRef.current) {
      context.current = canvasRef.current.getContext('2d');
    }
  }, []);

  useEffect(() => {
    const chIdleSrcSet = [
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_0.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_1.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_2.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_3.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_4.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_idle_5.png`,
    ];
    const chRunSrcSet = [
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_0.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_1.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_2.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_3.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_4.png`,
      `https://cdn2.embedgames.app/RUNNER/assets/${character}_run_5.png`,
    ];
    const chJumpSrcSet = [`https://cdn2.embedgames.app/RUNNER/assets/${character}_jump_0.png`];
    const cloudSrc = 'https://cdn2.embedgames.app/RUNNER/assets/cloud.png';

    loadTexture(groundImgSrc, setGroundImage);
    loadTexture(backgroundImgSrc, setBackgroundImage);
    loadTexture(cloudSrc, setCloudImage);
    loadTextures(chIdleSrcSet, setCharacterIdleImages);
    loadTextures(chRunSrcSet, setCharacterRunImages);
    loadTextures(chJumpSrcSet, setCharacterJumpImages);
    loadTexture(groundImgSrc, setEnvImage);
  }, [backgroundImgSrc, character, groundImgSrc]);

  const drawCharacter = useCallback(() => {
    if (context.current) {
      drawImage(context.current, currentCharacterImage.current, characterPosition.current, [
        CHARACTER_SIZE[0],
        CHARACTER_SIZE[1],
      ]);
    }
  }, []);

  const drawCloud = useCallback(() => {
    if (context.current) {
      drawImage(context.current, cloudImage, cloudPosition.current, [CLOUD_SIZE[0], CLOUD_SIZE[1]]);
    }
  }, [cloudImage]);

  const fillEnvs = useCallback(() => {
    envInfo.current = [];

    let currentPosition = canvasSize[0];

    const blockAmountOnFirstLevel = 3;
    const maxBlockOnLevel = 15;
    const mult = blockAmountOnFirstLevel * level.current;
    const max = mult >= maxBlockOnLevel ? maxBlockOnLevel : mult;

    for (let i = 0; i < max; i++) {
      const [x, y, offset] = getEnvSize(level.current);
      const position: [number, number] = [currentPosition, groundY + offset];

      envInfo.current.push({
        position,
        size: [x, y],
        env: envImage as HTMLImageElement,
      });

      let min: number;
      let max: number;

      if (level.current < 4) {
        min = 200;
        max = 600;
      } else if (level.current < 6) {
        min = 400;
        max = 600;
      } else {
        min = 600;
        max = 600;
      }

      currentPosition += getRandomInteger(min, max);
    }
  }, [canvasSize, envImage, groundY]);

  const drawEnvs = useCallback(() => {
    envInfo.current.forEach(({ position, size, env }) => {
      if (context.current) {
        drawImage(context.current, env, position, size);
      }
    });
  }, []);

  const texturesLoaded =
    Boolean(groundImage) &&
    Boolean(backgroundImage) &&
    Boolean(cloudImage) &&
    Boolean(envImage) &&
    characterIdleImages.length > 0 &&
    characterRunImages.length > 0 &&
    characterJumpImages.length > 0;

  const renderScene = useCallback(() => {
    if (context.current) {
      context.current.clearRect(0, 0, canvasSize[0], canvasSize[1]);
      drawBackground(context.current, backgroundImage, canvasSize);
      drawGround(context.current, groundImage, canvasSize);
      drawCloud();
      drawEnvs();
      drawCharacter();
    }
  }, [backgroundImage, canvasSize, drawCharacter, drawCloud, drawEnvs, groundImage]);

  useEffect(() => {
    if (context.current && texturesLoaded) {
      characterPosition.current = [0, groundY];

      if (envInfo.current.length === 0) {
        fillEnvs();
      }
    }
  }, [fillEnvs, groundY, texturesLoaded]);

  const getNextYCloudPosition = useCallback(() => {
    switch (cloudPosition.current[1]) {
      case -20:
        return -40;
      case -40:
        return -20;
      default:
        return 0;
    }
  }, []);

  const onClick = useCallback(async () => {
    if (!started && texturesLoaded) {
      if (await startGame()) {
        startJump.current = Date.now();
        characterState.current = 'JUMP';
      }
    }

    if (started) {
      if (characterState.current !== 'JUMP') {
        startJump.current = Date.now();
        characterState.current = 'JUMP';
      }
    }
  }, [startGame, started, texturesLoaded]);

  /**
   * Анимация персонажа в зависимости от его состояния и скорости
   */
  useEffect(() => {
    let prev = Date.now();
    let index = 0;

    const setCharacterImage = () => {
      if (crash.current) {
        characterRequestId.current = 0;
        return;
      }

      const now = Date.now();
      const diff = now - prev;

      const mult = 70 - level.current * 5;
      const maxDiff = mult < 20 ? 20 : mult;

      if (diff > maxDiff && context.current) {
        prev = now;

        let srcSet = characterSrcSet.get(characterState.current);

        if (srcSet) {
          if (index >= srcSet.length - 1) {
            index = 0;
          } else {
            index++;
          }

          currentCharacterImage.current = srcSet[index];
        }

        renderScene();
      }

      characterRequestId.current = requestAnimationFrame(setCharacterImage);
    };

    if (!characterRequestId.current && texturesLoaded) {
      characterRequestId.current = requestAnimationFrame(setCharacterImage);
    }
  }, [characterSrcSet, characterState, texturesLoaded, renderScene]);

  /**
   * Главный цикл фреймов
   */
  useEffect(() => {
    const y0 = characterPosition.current[1];
    let prevY = y0;

    const render = () => {
      const v0 = 0.8 * Math.sqrt(level.current); // начальная скорость
      const g = 0.0015 * level.current; // ускорение свободного падения
      const speed = level.current + 2;

      /**
       * Обработка столкновения
       */
      const [w, h] = [40, 50]; // real caharacter size in png
      const x1 = characterPosition.current[0] + 30;
      const x2 = x1 + w;
      const y1 = characterPosition.current[1] + 30;
      const y2 = y1 + h;

      const characterRect: Rect = { x1, y1, x2, y2 };

      for (let i = 0; i < envInfo.current.length; i++) {
        const {
          position: [x1, y1],
          size: [w, h],
        } = envInfo.current[i];

        const envRect: Rect = {
          x1,
          y1,
          x2: x1 + w,
          y2: y1 + h,
        };

        if (intersects(characterRect, envRect)) {
          crash.current = true;
          break;
        }

        if (envRect.x2 <= 0 && i > lastGoingEnvIndex.current) {
          lastGoingEnvIndex.current = i;
          setScore((score) => {
            const newScore = score + 1;

            if (newScore > maxScore.current) {
              maxScore.current = newScore;
            }

            return newScore;
          });
        }
      }

      if (crash.current) {
        setCrashScreenVisible(true);
        setAttempt((attempt) => attempt + 1);
        mainRequestId.current = 0;
        return;
      }

      /**
       * Препятствия
       */
      envInfo.current = envInfo.current.map((info) => {
        info.position[0] -= speed;

        return info;
      });

      /**
       * Облако
       */
      const x = cloudPosition.current[0] - 0.5;

      if (x + CLOUD_SIZE[0] < 0) {
        const y = getNextYCloudPosition();
        cloudPosition.current = [canvasSize[0], y];
      } else {
        cloudPosition.current = [x, cloudPosition.current[1]];
      }

      /**
       * Прыжок персонажа
       */
      if (characterState.current === 'JUMP') {
        const t = Date.now() - startJump.current;
        const y = y0 - v0 * t + (g * t * t) / 2;
        prevY = characterPosition.current[1];

        // летит вниз и достиг земли
        if (y > prevY && y >= groundY) {
          characterPosition.current = [characterPosition.current[0], groundY];
          characterState.current = 'RUN';
        } else {
          characterPosition.current = [characterPosition.current[0], y];
        }
      }

      /**
       * Если блоки закончились, поднимаем уровень, и генерируем новые блоки
       */
      if (
        envInfo.current[envInfo.current.length - 1]?.position[0] < 0 &&
        characterState.current !== 'JUMP'
      ) {
        level.current++;
        lastGoingEnvIndex.current = -1;
        fillEnvs();
      }

      mainRequestId.current = requestAnimationFrame(render);
      renderScene();
    };

    if (!mainRequestId.current && started && !crashScreenVisible) {
      mainRequestId.current = requestAnimationFrame(render);
    }
  }, [
    canvasSize,
    groundY,
    started,
    score,
    crashScreenVisible,
    getNextYCloudPosition,
    renderScene,
    fillEnvs,
  ]);

  /**
   * cancelAnimationFrame
   */
  useEffect(() => {
    return () => {
      cancelAnimationFrame(mainRequestId.current);
      cancelAnimationFrame(characterRequestId.current);
    };
  }, []);

  const onRetryClick = useCallback(() => {
    crash.current = false;
    level.current = 1;
    characterPosition.current = [0, groundY];
    lastGoingEnvIndex.current = -1;
    fillEnvs();
    setCrashScreenVisible(false);
    setScore(0);
  }, [fillEnvs, groundY]);

  const getScoreStyles = (): CSSProperties => {
    let color = '#67E0E8';
    let backgroundColor = '#874719';

    if (groundImgSrc.includes('ground_1')) {
      backgroundColor = '#A34023';
    }

    if (groundImgSrc.includes('ground_2')) {
      backgroundColor = '#3822A1';
    }

    if (backgroundImgSrc.includes('background_1')) {
      color = '#E8B682';
    }

    if (backgroundImgSrc.includes('background_2')) {
      color = '#4F516C';
    }

    return {
      color,
      backgroundColor,
    };
  };

  const onGetPrizeClick = useCallback(() => {
    if (!getPrizeInProgress) {
      setGetPrizeInProgress(true);
      endGame({ score: maxScore.current }).then(() => {
        setGetPrizeInProgress(false);
      });
    }
  }, [getPrizeInProgress, endGame]);

  const getFinishGameText = (): string => {
    if (mainGameSettings.campaignType === 'competition') {
      return 'Зачесть результат';
    }

    return 'Получить приз';
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef} onTouchStart={onClick} onMouseDown={onClick}>
      <canvas id="eg-canvas" width={canvasSize[0]} height={canvasSize[1]} ref={canvasRef} />

      {!texturesLoaded && (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      )}

      {!started && (
        <div className={styles.splashScreen}>
          Кликните для старта.
          <br />У вас будет {MAX_ATTEMPTS}{' '}
          {pluralize(MAX_ATTEMPTS, 'попытка', 'попытки', 'попыток')}.
        </div>
      )}

      {crashScreenVisible && (
        <div className={styles.crashScreen}>
          {attempt < MAX_ATTEMPTS ? (
            <>
              <h3 className={styles.attempt}>
                Очки: <span>{score}</span>
              </h3>
              <h3 className={styles.attempt}>
                Осталось попыток: <span>{MAX_ATTEMPTS - attempt}</span>
              </h3>
            </>
          ) : (
            <h3 className={styles.attempt}>
              Лучший результат: <span>{maxScore.current}</span>
            </h3>
          )}

          <div className={styles.buttonsWrap}>
            {attempt < MAX_ATTEMPTS ? (
              <>
                <button className={cn(styles.retryButton, styles.button)} onClick={onRetryClick}>
                  Сыграть еще раз
                </button>

                <button
                  className={cn(styles.getPrizeButton, styles.button)}
                  onClick={onGetPrizeClick}
                >
                  {getPrizeInProgress ? <Loader /> : getFinishGameText()}
                </button>
              </>
            ) : (
              <button
                className={cn(styles.button, styles.getPrizeButton)}
                onClick={onGetPrizeClick}
              >
                {getPrizeInProgress ? <Loader /> : getFinishGameText()}
              </button>
            )}
          </div>
        </div>
      )}

      {started && !crashScreenVisible && (
        <div style={getScoreStyles()} className={styles.score}>
          {formatScore(score)}
        </div>
      )}
    </div>
  );
};
